import type {
  Machine,
  MachineStatus,
  Command,
  PaginatedResponse,
  ApiResponse,
  WorkflowDefinition,
  WorkflowRecord,
  TriggerRecord,
  ExecutionRecord,
  StepResultRecord,
} from '@foxgrid/core';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class FoxGridAPI {
  private getToken(): string | null {
    return localStorage.getItem('foxgrid_token');
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();

    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.error?.message || 'Request failed',
        response.status,
        data.error?.code
      );
    }

    return data;
  }

  // Auth
  async requestMagicLink(email: string): Promise<{ success: boolean; message: string }> {
    return this.request('/api/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyToken(token: string): Promise<{
    success: boolean;
    accessToken: string;
    user: {
      id: string;
      email: string;
      name: string | null;
      tenantId: string;
      tenantName: string;
      role: string;
    };
  }> {
    return this.request('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async register(data: {
    email: string;
    name?: string;
    companyName?: string;
  }): Promise<{
    success: boolean;
    message: string;
    tenantId: string;
    userId: string;
  }> {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCurrentUser(): Promise<
    ApiResponse<{
      id: string;
      email: string;
      name: string | null;
      tenantId: string;
      tenantName: string;
      role: string;
    }>
  > {
    return this.request('/api/auth/me');
  }

  async updateWorkspace(name: string): Promise<ApiResponse<{ name: string }>> {
    return this.request('/api/auth/workspace', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  // Machines
  async getMachines(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    osType?: string;
    online?: boolean;
  }): Promise<ApiResponse<PaginatedResponse<Machine>>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.osType) query.set('osType', params.osType);
    if (params?.online !== undefined) query.set('online', params.online.toString());

    return this.request(`/api/inventory/machines?${query}`);
  }

  async getMachine(id: string): Promise<ApiResponse<Machine>> {
    return this.request(`/api/inventory/machines/${id}`);
  }

  async getMachineStatus(id: string): Promise<ApiResponse<MachineStatus & { lastSeen: string }>> {
    return this.request(`/api/inventory/machines/${id}/status`);
  }

  async updateMachine(
    id: string,
    data: { tags?: string[]; projectId?: string | null }
  ): Promise<ApiResponse<void>> {
    return this.request(`/api/inventory/machines/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Commands
  async createCommand(
    machineId: string,
    type: string,
    params?: Record<string, unknown>
  ): Promise<ApiResponse<{ id: string; machineId: string; type: string; status: string }>> {
    return this.request('/api/commands', {
      method: 'POST',
      body: JSON.stringify({ machineId, type, params }),
    });
  }

  async getCommand(id: string): Promise<ApiResponse<Command>> {
    return this.request(`/api/commands/${id}`);
  }

  async getCommands(params?: {
    machineId?: string;
    status?: string;
    limit?: number;
  }): Promise<ApiResponse<Command[]>> {
    const query = new URLSearchParams();
    if (params?.machineId) query.set('machineId', params.machineId);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());

    return this.request(`/api/commands?${query}`);
  }

  // Chat
  async sendChatMessage(
    message: string,
    context?: { machineId?: string; projectId?: string }
  ): Promise<
    ApiResponse<{
      reply: string;
      intent: string;
      context?: unknown;
    }>
  > {
    return this.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ message, context }),
    });
  }

  // Projects
  async getProjects(): Promise<
    ApiResponse<{
      id: string;
      name: string;
      description: string | null;
      machineCount: number;
      workflowCount: number;
      createdAt: string;
      updatedAt: string;
    }[]>
  > {
    return this.request('/api/inventory/projects');
  }

  async createProject(data: {
    name: string;
    description?: string;
  }): Promise<ApiResponse<{ id: string; name: string; description: string | null }>> {
    return this.request('/api/inventory/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string): Promise<
    ApiResponse<{
      id: string;
      name: string;
      description: string | null;
      settings: Record<string, unknown>;
      machineCount: number;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return this.request(`/api/inventory/projects/${id}`);
  }

  async updateProject(
    id: string,
    data: { name?: string; description?: string | null; settings?: Record<string, unknown> }
  ): Promise<ApiResponse<void>> {
    return this.request(`/api/inventory/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return this.request(`/api/inventory/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Skills
  async getCustomSkills(): Promise<
    ApiResponse<{
      id: string;
      name: string;
      description: string;
      version: string;
      status: string;
      enabled: boolean;
      projectId: string | null;
      createdAt: string;
    }[]>
  > {
    return this.request('/api/skills/custom/list');
  }

  async createCustomSkill(data: {
    name: string;
    description?: string;
    code: string;
    projectId?: string;
    builderConfig?: {
      steps: Array<{
        id: string;
        type: string;
        config: Record<string, unknown>;
      }>;
      triggers: Array<{
        type: string;
        config: Record<string, unknown>;
      }>;
    };
    codeHash?: string;
  }): Promise<
    ApiResponse<{
      id: string;
      name: string;
      description: string;
      version: string;
      status: string;
    }>
  > {
    return this.request('/api/skills/custom', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomSkill(
    skillId: string,
    data: {
      name?: string;
      description?: string;
      code?: string;
      enabled?: boolean;
      builderConfig?: {
        steps: Array<{
          id: string;
          type: string;
          config: Record<string, unknown>;
        }>;
        triggers: Array<{
          type: string;
          config: Record<string, unknown>;
        }>;
      };
      codeHash?: string;
    }
  ): Promise<ApiResponse<void>> {
    return this.request(`/api/skills/custom/${skillId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomSkill(skillId: string): Promise<ApiResponse<void>> {
    return this.request(`/api/skills/custom/${skillId}`, {
      method: 'DELETE',
    });
  }

  async getCustomSkill(skillId: string): Promise<
    ApiResponse<{
      id: string;
      name: string;
      description: string;
      version: string;
      status: string;
      enabled: boolean;
      projectId: string | null;
      code: string;
      builderConfig?: {
        steps: Array<{
          id: string;
          type: string;
          config: Record<string, unknown>;
        }>;
        triggers: Array<{
          type: string;
          config: Record<string, unknown>;
        }>;
      };
      codeHash?: string;
      inputs: Array<{
        name: string;
        type: string;
        required?: boolean;
        default?: unknown;
        description?: string;
      }>;
      outputs: Array<{
        name: string;
        type: string;
        description?: string;
      }>;
      capabilities: string[];
      createdAt: string;
      updatedAt: string;
      executions: Array<{
        id: string;
        status: string;
        startedAt: string;
        completedAt: string | null;  // Maps from finished_at in DB
        durationMs: number | null;
        error: string | null;
      }>;
    }>
  > {
    return this.request(`/api/skills/custom/${skillId}`);
  }

  async testSkill(
    skillId: string,
    inputs: Record<string, unknown>
  ): Promise<
    ApiResponse<{
      executionId: string;
      success: boolean;
      data?: unknown;
      error?: { code: string; message: string };
      durationMs: number;
    }>
  > {
    return this.request(`/api/skills/${skillId}/execute`, {
      method: 'POST',
      body: JSON.stringify({ inputs, trigger: 'manual' }),
    });
  }

  // Credentials
  async getCredentials(): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        integrationId: string;
        metadata?: Record<string, unknown>;
        createdAt: string;
        lastUsedAt?: string;
        expiresAt?: string;
      }>
    >
  > {
    return this.request('/api/integrations/credentials/list');
  }

  // Workflows
  async getWorkflows(params?: {
    limit?: number;
    offset?: number;
    active?: boolean;
  }): Promise<ApiResponse<{ workflows: (WorkflowRecord & { triggers: TriggerRecord[] })[]; total: number }>> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', params.limit.toString());
    if (params?.offset !== undefined) query.set('offset', params.offset.toString());
    if (params?.active !== undefined) query.set('active', params.active.toString());

    return this.request(`/api/workflows?${query}`);
  }

  async getWorkflow(id: string): Promise<ApiResponse<{ workflow: WorkflowRecord; triggers: TriggerRecord[] }>> {
    return this.request(`/api/workflows/${id}`);
  }

  async createWorkflow(data: {
    name: string;
    description?: string;
    definition: WorkflowDefinition;
    triggers?: Array<{
      type: string;
      config: Record<string, unknown>;
      enabled?: boolean;
    }>;
  }): Promise<ApiResponse<{ workflow: WorkflowRecord; triggers: TriggerRecord[] }>> {
    return this.request('/api/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkflow(
    id: string,
    data: {
      name?: string;
      description?: string | null;
      definition?: WorkflowDefinition;
      active?: boolean;
      triggers?: Array<{
        type: string;
        config: Record<string, unknown>;
        enabled?: boolean;
      }>;
    }
  ): Promise<ApiResponse<{ workflow: WorkflowRecord; triggers: TriggerRecord[] }>> {
    return this.request(`/api/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activateWorkflow(id: string): Promise<ApiResponse<{ active: boolean }>> {
    return this.request(`/api/workflows/${id}/activate`, {
      method: 'PATCH',
    });
  }

  async deactivateWorkflow(id: string): Promise<ApiResponse<{ active: boolean }>> {
    return this.request(`/api/workflows/${id}/deactivate`, {
      method: 'PATCH',
    });
  }

  async deleteWorkflow(id: string): Promise<ApiResponse<void>> {
    return this.request(`/api/workflows/${id}`, {
      method: 'DELETE',
    });
  }

  async executeWorkflow(
    id: string,
    input?: Record<string, unknown>
  ): Promise<ApiResponse<{ executionId: string }>> {
    return this.request(`/api/workflows/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    });
  }

  async getWorkflowExecutions(
    id: string,
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
    }
  ): Promise<ApiResponse<{ executions: ExecutionRecord[]; total: number }>> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', params.limit.toString());
    if (params?.offset !== undefined) query.set('offset', params.offset.toString());
    if (params?.status) query.set('status', params.status);

    return this.request(`/api/workflows/${id}/executions?${query}`);
  }

  async getExecutionDetail(
    workflowId: string,
    executionId: string
  ): Promise<
    ApiResponse<{
      execution: ExecutionRecord & { workflowName: string };
      steps: StepResultRecord[];
    }>
  > {
    return this.request(`/api/workflows/${workflowId}/executions/${executionId}`);
  }

  async getExecutionSteps(
    workflowId: string,
    executionId: string
  ): Promise<ApiResponse<{ steps: StepResultRecord[] }>> {
    return this.request(`/api/workflows/${workflowId}/executions/${executionId}/steps`);
  }

  async generateWorkflowFromNL(
    description: string,
    availableIntegrations?: Record<string, unknown>[]
  ): Promise<ApiResponse<{
    name: string;
    description: string;
    definition: WorkflowDefinition;
    trigger: { id: string; type: string; config: Record<string, unknown>; enabled: boolean };
  }>> {
    return this.request('/api/ai/workflows/generate', {
      method: 'POST',
      body: JSON.stringify({
        description,
        available_integrations: availableIntegrations ?? [],
      }),
    });
  }

  async testWorkflowStep(data: {
    node: { id: string; type: string; label: string; config: Record<string, unknown>; position: { x: number; y: number } };
    mockContext?: {
      triggerData?: unknown;
      stepResults?: Record<string, { output: unknown }>;
      variables?: Record<string, unknown>;
    };
  }): Promise<ApiResponse<{ status: string; output: unknown; error?: string; durationMs: number }>> {
    return this.request('/api/workflows/test-step', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // AI Skill Execution (SSE streaming -- returns raw Response, not parsed JSON)

  /**
   * Execute a skill with AI reasoning via SSE streaming.
   * Returns raw Response so the caller can read the stream.
   */
  async executeSkillAI(
    skillId: string,
    inputs: Record<string, unknown>,
    manifest?: Record<string, unknown>
  ): Promise<Response> {
    const token = this.getToken();
    const body: Record<string, unknown> = { inputs, trigger: 'manual' };
    if (manifest) body.manifest = manifest;

    return fetch(`${API_BASE_URL}/api/ai/skills/${encodeURIComponent(skillId)}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
  }

  /**
   * Approve or reject a skill execution (HITL decision).
   * Returns raw Response for SSE streaming of the remaining execution.
   */
  async approveSkill(
    skillId: string,
    threadId: string,
    approved: boolean,
    reason?: string
  ): Promise<Response> {
    const token = this.getToken();

    return fetch(`${API_BASE_URL}/api/ai/skills/${encodeURIComponent(skillId)}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ thread_id: threadId, approved, reason }),
    });
  }

  /**
   * Execute a chain of skills via SSE streaming.
   * Returns raw Response so the caller can read the stream.
   */
  async executeSkillChain(
    skills: Array<{ skill_id: string; inputs: Record<string, unknown> }>
  ): Promise<Response> {
    const token = this.getToken();

    return fetch(`${API_BASE_URL}/api/ai/skills/chain/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ skills }),
    });
  }

  /** Get confidence threshold configuration */
  async getConfidenceConfig(): Promise<ApiResponse<{
    default_threshold: number;
    skill_overrides: Record<string, number>;
  }>> {
    return this.request('/api/ai/skills/confidence-config');
  }

  /** Update confidence threshold configuration */
  async updateConfidenceConfig(config: {
    default_threshold: number;
    skill_overrides: Record<string, number>;
  }): Promise<ApiResponse<{
    default_threshold: number;
    skill_overrides: Record<string, number>;
  }>> {
    return this.request('/api/ai/skills/confidence-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Reasoning Traces
  async getTraces(skillId?: string, limit?: number, offset?: number): Promise<any> {
    const query = new URLSearchParams();
    if (skillId) query.set('skill_id', skillId);
    if (limit !== undefined) query.set('limit', limit.toString());
    if (offset !== undefined) query.set('offset', offset.toString());

    return this.request(`/api/ai/traces?${query}`);
  }

  async getTrace(traceId: string): Promise<any> {
    return this.request(`/api/ai/traces/${traceId}`);
  }

  async getTraceByExecution(executionId: string): Promise<any> {
    return this.request(`/api/ai/traces/by-execution/${executionId}`);
  }

  // Generic HTTP methods for flexible API calls
  async get<T = unknown>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(`/api${path.startsWith('/') ? path : `/${path}`}`, {
      ...options,
      method: 'GET',
    });
  }

  async post<T = unknown>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(`/api${path.startsWith('/') ? path : `/${path}`}`, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T = unknown>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(`/api${path.startsWith('/') ? path : `/${path}`}`, {
      ...options,
      method: 'DELETE',
    });
  }
}

// Singleton instance
export const api = new FoxGridAPI();
